import React, { useState, useEffect } from 'react';
import { X, Calendar, User, ChevronRight, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { apiService } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { formatUTCDate } from '../../utils/dateHelpers';

interface VisitChainModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitId: string;
}

interface ChainNode {
  id: string;
  visitDate: string;
  visitType: string;
  status: string;
  visitedBy: { name: string };
  purpose?: string;
  outcome?: string;
  nextVisitRequired: boolean;
  nextVisitDate?: string;
  followUpHandled: boolean;
  depth: number;
  children: ChainNode[];
}

export const VisitChainModal: React.FC<VisitChainModalProps> = ({
  isOpen,
  onClose,
  visitId
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chain, setChain] = useState<ChainNode | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [currentVisitId, setCurrentVisitId] = useState<string>('');

  useEffect(() => {
    if (isOpen && visitId) {
      fetchVisitChain();
    }
  }, [isOpen, visitId]);

  const fetchVisitChain = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getVisitChain(visitId);
      setChain(response.chain);
      setStats(response.stats);
      setCurrentVisitId(response.currentVisitId);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load visit chain');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no-show':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderNode = (node: ChainNode, isLast: boolean = false) => {
    const isCurrentVisit = node.id === currentVisitId;
    
    return (
      <div key={node.id} className="relative">
        {/* Connection line */}
        {node.depth > 0 && (
          <div className="absolute left-4 -top-4 w-0.5 h-4 bg-gray-300" />
        )}
        
        {/* Node content */}
        <div className={`flex items-start gap-3 p-3 rounded-lg ${isCurrentVisit ? 'bg-blue-50 border-2 border-blue-500' : 'bg-gray-50'}`}>
          {/* Timeline dot */}
          <div className="relative">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              node.status === 'completed' ? 'bg-green-500' : 
              node.status === 'scheduled' ? 'bg-blue-500' : 'bg-gray-400'
            } text-white text-xs font-bold`}>
              {node.depth + 1}
            </div>
            {/* Vertical line to children */}
            {node.children.length > 0 && (
              <div className="absolute left-4 top-8 w-0.5 h-4 bg-gray-300" />
            )}
          </div>
          
          {/* Visit details */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {formatUTCDate(node.visitDate)}
                </span>
                <Badge className={getStatusColor(node.status)}>
                  {node.status}
                </Badge>
                {node.visitType === 'follow-up' && (
                  <Badge variant="outline">Follow-up</Badge>
                )}
                {isCurrentVisit && (
                  <Badge className="bg-blue-500 text-white">Current</Badge>
                )}
              </div>
            </div>
            
            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {node.visitedBy?.name || 'Unknown'}
              </div>
              {node.purpose && (
                <div>Purpose: {node.purpose}</div>
              )}
              {node.outcome && (
                <div>Outcome: {node.outcome}</div>
              )}
              {node.nextVisitRequired && (
                <div className="flex items-center gap-1 text-orange-600">
                  <AlertCircle className="h-3 w-3" />
                  Follow-up required
                  {node.nextVisitDate && ` - ${formatUTCDate(node.nextVisitDate)}`}
                  {node.followUpHandled && ' (Scheduled)'}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Render children */}
        {node.children.length > 0 && (
          <div className="ml-8 mt-4">
            {node.children.map((child, index) => 
              renderNode(child, index === node.children.length - 1)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Visit Chain Timeline</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="text-center py-8">Loading visit chain...</div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && chain && (
          <div className="space-y-6">
            {/* Statistics */}
            {stats && (
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Total Visits</div>
                  <div className="text-2xl font-bold">{stats.totalVisits}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Chain Depth</div>
                  <div className="text-2xl font-bold">{stats.maxDepth + 1}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Completed</div>
                  <div className="text-2xl font-bold text-green-600">{stats.completedVisits}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Pending Follow-ups</div>
                  <div className="text-2xl font-bold text-orange-600">{stats.pendingFollowUps}</div>
                </div>
              </div>
            )}

            {/* Chain warning */}
            {stats && stats.maxDepth >= 3 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This visit chain has {stats.maxDepth + 1} levels. Consider starting a new initial visit instead of continuing the chain.
                </AlertDescription>
              </Alert>
            )}

            {/* Visit chain visualization */}
            <div className="space-y-4">
              {renderNode(chain)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
